import React, { useEffect, useMemo, useState, useCallback } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Navigate } from "react-router-dom";
import { ADMIN } from "@/constants/testIds";
import { toast } from "sonner";
import {
  Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription,
} from "@/components/ui/sheet";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Users, Inbox, Calendar as CalendarIcon, Search, RefreshCw, Trash2,
  ExternalLink, Mail, Phone, MapPin, Sparkles,
} from "lucide-react";

const STATUS_META = {
  new:       { label: "New",       color: "#6B93B8", bg: "rgba(107,147,184,0.14)" },
  contacted: { label: "Contacted", color: "#8A6D3B", bg: "rgba(196,164,132,0.22)" },
  booked:    { label: "Booked",    color: "#3E7A54", bg: "rgba(93,155,110,0.18)" },
  archived:  { label: "Archived",  color: "#6E6E6E", bg: "rgba(110,110,110,0.14)" },
};

const BOOKING_STATUS_META = {
  confirmed: { label: "Confirmed", color: "#3E7A54", bg: "rgba(93,155,110,0.18)" },
  completed: { label: "Completed", color: "#6B93B8", bg: "rgba(107,147,184,0.14)" },
  cancelled: { label: "Cancelled", color: "#B4553F", bg: "rgba(180,85,63,0.14)" },
};

function StatusPill({ meta }) {
  return (
    <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium"
          style={{ background: meta.bg, color: meta.color }}>
      <span className="w-1.5 h-1.5 rounded-full" style={{ background: meta.color }} />
      {meta.label}
    </span>
  );
}

function formatDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString(undefined, {
      day: "2-digit", month: "short", year: "numeric",
      hour: "2-digit", minute: "2-digit",
    });
  } catch { return iso; }
}

export default function Admin() {
  const { user, loading } = useAuth();

  if (loading) return <div className="min-h-[60vh] grid place-items-center text-sm" style={{ color: "var(--ev-muted)" }}>Loading…</div>;
  if (!user) return <Navigate to="/login" replace />;
  if (user.role !== "admin") return <Navigate to="/dashboard" replace />;

  return <AdminInner />;
}

function AdminInner() {
  const [tab, setTab] = useState("inquiries");
  const [stats, setStats] = useState(null);
  const [inquiries, setInquiries] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [statusFilter, setStatusFilter] = useState("all");
  const [bookingScope, setBookingScope] = useState("all");
  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState(null); // inquiry drawer
  const [confirmDeleteId, setConfirmDeleteId] = useState(null);
  const [busy, setBusy] = useState(false);

  const loadStats = useCallback(async () => {
    try { const { data } = await api.get("/admin/stats"); setStats(data); } catch { /* ignore */ }
  }, []);

  const loadInquiries = useCallback(async () => {
    setBusy(true);
    try {
      const params = {};
      if (statusFilter && statusFilter !== "all") params.status = statusFilter;
      if (search.trim()) params.q = search.trim();
      const { data } = await api.get("/admin/inquiries", { params });
      setInquiries(data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to load inquiries");
    } finally { setBusy(false); }
  }, [statusFilter, search]);

  const loadBookings = useCallback(async () => {
    setBusy(true);
    try {
      const params = {};
      if (bookingScope && bookingScope !== "all") params.scope = bookingScope;
      if (search.trim()) params.q = search.trim();
      const { data } = await api.get("/admin/bookings", { params });
      setBookings(data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to load bookings");
    } finally { setBusy(false); }
  }, [bookingScope, search]);

  useEffect(() => { loadStats(); }, [loadStats]);
  useEffect(() => { if (tab === "inquiries") loadInquiries(); }, [tab, loadInquiries]);
  useEffect(() => { if (tab === "bookings") loadBookings(); }, [tab, loadBookings]);

  const refresh = () => {
    loadStats();
    if (tab === "inquiries") loadInquiries(); else loadBookings();
  };

  const updateInquiry = async (id, updates) => {
    try {
      const { data } = await api.patch(`/admin/inquiries/${id}`, updates);
      setInquiries((rows) => rows.map((r) => (r.id === id ? data : r)));
      if (selected?.id === id) setSelected(data);
      loadStats();
      toast.success("Updated");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Update failed");
    }
  };

  const deleteInquiry = async (id) => {
    try {
      await api.delete(`/admin/inquiries/${id}`);
      setInquiries((rows) => rows.filter((r) => r.id !== id));
      setConfirmDeleteId(null);
      if (selected?.id === id) setSelected(null);
      loadStats();
      toast.success("Inquiry deleted");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Delete failed");
    }
  };

  const updateBooking = async (id, updates) => {
    try {
      const { data } = await api.patch(`/admin/bookings/${id}`, updates);
      setBookings((rows) => rows.map((r) => (r.id === id ? data : r)));
      loadStats();
      toast.success("Updated");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Update failed");
    }
  };

  const kpis = useMemo(() => ([
    { label: "New inquiries", value: stats?.inquiries?.new ?? "—", icon: Inbox, tint: "#6B93B8" },
    { label: "Contacted", value: stats?.inquiries?.contacted ?? "—", icon: Mail, tint: "#8A6D3B" },
    { label: "Booked", value: stats?.inquiries?.booked ?? "—", icon: Sparkles, tint: "#3E7A54" },
    { label: "Upcoming sessions", value: stats?.bookings?.upcoming ?? "—", icon: CalendarIcon, tint: "#6B93B8" },
    { label: "Clients", value: stats?.users ?? "—", icon: Users, tint: "#6E6E6E" },
  ]), [stats]);

  return (
    <div data-testid={ADMIN.root} className="mx-auto max-w-[1300px] px-5 md:px-10 py-10 md:py-14">
      <div className="flex flex-wrap items-end justify-between gap-4 mb-8">
        <div>
          <div className="eyebrow">Admin console</div>
          <h1 className="font-serif-ev text-4xl md:text-5xl mt-3 leading-[1.02] tracking-tight">
            The <span className="italic" style={{ color: "var(--ev-blue-2)" }}>studio</span> desk.
          </h1>
          <p className="mt-2 text-sm" style={{ color: "var(--ev-muted)" }}>
            Review inquiries, respond warmly, and keep your calendar in order.
          </p>
        </div>
        <button
          data-testid={ADMIN.refresh}
          onClick={refresh}
          className="inline-flex items-center gap-2 px-4 py-2 border hairline rounded-full bg-white text-sm smooth-hover"
        >
          <RefreshCw className={`w-4 h-4 ${busy ? "animate-spin" : ""}`} /> Refresh
        </button>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4 md:gap-5 mb-10">
        {kpis.map((k) => (
          <div key={k.label} className="ev-card p-5">
            <k.icon className="w-4 h-4" style={{ color: k.tint }} />
            <div className="font-serif-ev text-3xl mt-3">{k.value}</div>
            <div className="eyebrow mt-1 !text-[10px]">{k.label}</div>
          </div>
        ))}
      </div>

      {/* Tabs */}
      <div className="flex gap-2 border-b hairline mb-6">
        {[
          { id: "inquiries", label: "Inquiries", testid: ADMIN.tabInquiries },
          { id: "bookings",  label: "Bookings",  testid: ADMIN.tabBookings  },
        ].map((t) => (
          <button
            key={t.id}
            data-testid={t.testid}
            onClick={() => setTab(t.id)}
            className={`px-4 py-3 text-sm font-medium border-b-2 -mb-px transition ${tab === t.id ? "" : "opacity-50"}`}
            style={{ borderColor: tab === t.id ? "var(--ev-blue-2)" : "transparent",
                     color: tab === t.id ? "var(--ev-ink)" : "var(--ev-muted)" }}
          >
            {t.label}
          </button>
        ))}
      </div>

      {/* Controls */}
      <div className="flex flex-wrap items-center gap-3 mb-5">
        <div className="flex items-center gap-2 flex-1 min-w-[220px] max-w-[420px] bg-white border hairline rounded-full px-4 py-2">
          <Search className="w-4 h-4" style={{ color: "var(--ev-muted)" }} />
          <input
            data-testid={ADMIN.search}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search name, email, phone…"
            className="flex-1 bg-transparent outline-none text-sm"
          />
        </div>
        {tab === "inquiries" ? (
          <div className="flex gap-2 flex-wrap">
            {[
              ["all",       "All",       ADMIN.filterAll],
              ["new",       "New",       ADMIN.filterNew],
              ["contacted", "Contacted", ADMIN.filterContacted],
              ["booked",    "Booked",    ADMIN.filterBooked],
              ["archived",  "Archived",  ADMIN.filterArchived],
            ].map(([k, l, tid]) => (
              <button
                key={k}
                data-testid={tid}
                onClick={() => setStatusFilter(k)}
                className={`px-3 py-1.5 rounded-full text-xs border hairline ${statusFilter === k ? "bg-[color:var(--ev-blue-2)] text-white border-transparent" : "bg-white"}`}
              >
                {l}
              </button>
            ))}
          </div>
        ) : (
          <div className="flex gap-2 flex-wrap">
            {[["all","All"],["upcoming","Upcoming"],["past","Past"]].map(([k,l]) => (
              <button key={k} onClick={() => setBookingScope(k)}
                      className={`px-3 py-1.5 rounded-full text-xs border hairline ${bookingScope === k ? "bg-[color:var(--ev-blue-2)] text-white border-transparent" : "bg-white"}`}>
                {l}
              </button>
            ))}
          </div>
        )}
      </div>

      {tab === "inquiries" ? (
        <InquiriesTable
          rows={inquiries}
          onOpen={(row) => setSelected(row)}
          onDelete={(id) => setConfirmDeleteId(id)}
          onStatusChange={(id, status) => updateInquiry(id, { status })}
        />
      ) : (
        <BookingsTable
          rows={bookings}
          onStatusChange={(id, status) => updateBooking(id, { status })}
        />
      )}

      {/* Inquiry drawer */}
      <Sheet open={!!selected} onOpenChange={(open) => !open && setSelected(null)}>
        <SheetContent className="w-full sm:max-w-lg bg-[color:var(--ev-bg)] border-none flex flex-col">
          {selected && (
            <InquiryDrawer
              inquiry={selected}
              onSave={(updates) => updateInquiry(selected.id, updates)}
              onDelete={() => setConfirmDeleteId(selected.id)}
              onClose={() => setSelected(null)}
            />
          )}
        </SheetContent>
      </Sheet>

      {/* Delete confirm */}
      <AlertDialog open={!!confirmDeleteId} onOpenChange={(o) => !o && setConfirmDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="font-serif-ev text-2xl">Delete this inquiry?</AlertDialogTitle>
            <AlertDialogDescription>
              This action is permanent. The record will be removed from your studio desk.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={() => deleteInquiry(confirmDeleteId)}
                               className="bg-[color:var(--ev-blue-2)] hover:bg-[color:var(--ev-blue-3)]">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function InquiriesTable({ rows, onOpen, onDelete, onStatusChange }) {
  if (!rows.length) {
    return (
      <div className="ev-card p-10 text-center">
        <Inbox className="w-6 h-6 mx-auto" style={{ color: "var(--ev-muted)" }} />
        <div className="font-serif-ev text-2xl mt-4">No inquiries yet.</div>
        <div className="text-sm mt-1" style={{ color: "var(--ev-muted)" }}>
          When someone submits the booking form, they'll appear here.
        </div>
      </div>
    );
  }
  return (
    <div className="ev-card overflow-hidden">
      {/* Desktop table */}
      <div className="hidden md:block">
        <div className="grid grid-cols-12 text-xs uppercase tracking-widest py-3 px-5 border-b hairline"
             style={{ color: "var(--ev-muted)" }}>
          <div className="col-span-3">Contact</div>
          <div className="col-span-2">Location</div>
          <div className="col-span-2">Focus</div>
          <div className="col-span-2">Received</div>
          <div className="col-span-2">Status</div>
          <div className="col-span-1 text-right">Actions</div>
        </div>
        <div className="divide-y hairline">
          {rows.map((r) => (
            <div key={r.id} data-testid={ADMIN.inquiryRow(r.id)}
                 className="grid grid-cols-12 items-center py-4 px-5 text-sm hover:bg-white/60 transition">
              <button data-testid={ADMIN.inquiryOpen(r.id)} onClick={() => onOpen(r)}
                      className="col-span-3 text-left">
                <div className="font-medium" style={{ color: "var(--ev-ink)" }}>{r.name}</div>
                <div className="text-xs mt-0.5 truncate" style={{ color: "var(--ev-muted)" }}>{r.email}</div>
              </button>
              <div className="col-span-2 truncate" style={{ color: "var(--ev-muted)" }}>{r.location || "—"}</div>
              <div className="col-span-2 truncate" style={{ color: "var(--ev-muted)" }}>{r.area_of_interest || r.intention || "—"}</div>
              <div className="col-span-2" style={{ color: "var(--ev-muted)" }}>{formatDate(r.created_at)}</div>
              <div className="col-span-2">
                <Select value={r.status || "new"} onValueChange={(v) => onStatusChange(r.id, v)}>
                  <SelectTrigger data-testid={ADMIN.inquiryStatus(r.id)}
                                 className="rounded-full h-8 py-0 border hairline bg-white text-xs">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {Object.keys(STATUS_META).map((k) => (
                      <SelectItem key={k} value={k}>{STATUS_META[k].label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="col-span-1 flex justify-end gap-1">
                <button onClick={() => onOpen(r)} className="p-2 rounded-full hover:bg-white" aria-label="Open">
                  <ExternalLink className="w-4 h-4" style={{ color: "var(--ev-blue-2)" }} />
                </button>
                <button data-testid={ADMIN.inquiryDelete(r.id)}
                        onClick={() => onDelete(r.id)}
                        className="p-2 rounded-full hover:bg-white" aria-label="Delete">
                  <Trash2 className="w-4 h-4" style={{ color: "#B4553F" }} />
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Mobile list */}
      <div className="md:hidden divide-y hairline">
        {rows.map((r) => (
          <button
            key={r.id}
            data-testid={ADMIN.inquiryRow(r.id)}
            onClick={() => onOpen(r)}
            className="w-full text-left p-4 flex flex-col gap-2"
          >
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <div className="font-medium truncate" style={{ color: "var(--ev-ink)" }}>{r.name}</div>
                <div className="text-xs truncate" style={{ color: "var(--ev-muted)" }}>{r.email}</div>
              </div>
              <StatusPill meta={STATUS_META[r.status || "new"]} />
            </div>
            <div className="text-xs flex flex-wrap gap-3" style={{ color: "var(--ev-muted)" }}>
              {r.location && <span className="inline-flex items-center gap-1"><MapPin className="w-3 h-3" />{r.location}</span>}
              {r.area_of_interest && <span className="inline-flex items-center gap-1"><Sparkles className="w-3 h-3" />{r.area_of_interest}</span>}
              <span>{formatDate(r.created_at)}</span>
            </div>
          </button>
        ))}
      </div>
    </div>
  );
}

function BookingsTable({ rows, onStatusChange }) {
  if (!rows.length) {
    return (
      <div className="ev-card p-10 text-center">
        <CalendarIcon className="w-6 h-6 mx-auto" style={{ color: "var(--ev-muted)" }} />
        <div className="font-serif-ev text-2xl mt-4">No bookings yet.</div>
        <div className="text-sm mt-1" style={{ color: "var(--ev-muted)" }}>
          Sessions confirmed by clients will appear here.
        </div>
      </div>
    );
  }
  return (
    <div className="ev-card overflow-hidden">
      <div className="hidden md:block">
        <div className="grid grid-cols-12 text-xs uppercase tracking-widest py-3 px-5 border-b hairline"
             style={{ color: "var(--ev-muted)" }}>
          <div className="col-span-3">Client</div>
          <div className="col-span-3">Session</div>
          <div className="col-span-2">Date</div>
          <div className="col-span-2">Time</div>
          <div className="col-span-2">Status</div>
        </div>
        <div className="divide-y hairline">
          {rows.map((r) => (
            <div key={r.id} data-testid={ADMIN.bookingRow(r.id)}
                 className="grid grid-cols-12 items-center py-4 px-5 text-sm">
              <div className="col-span-3">
                <div className="font-medium">{r.user_name}</div>
                <div className="text-xs mt-0.5 truncate" style={{ color: "var(--ev-muted)" }}>{r.user_email}</div>
              </div>
              <div className="col-span-3">
                <div className="font-serif-ev text-base leading-tight">{r.program_title || "Consultation"}</div>
                {r.intention && <div className="text-xs mt-0.5 line-clamp-1" style={{ color: "var(--ev-muted)" }}>{r.intention}</div>}
              </div>
              <div className="col-span-2" style={{ color: "var(--ev-muted)" }}>
                {new Date(r.date).toLocaleDateString(undefined, { day: "2-digit", month: "short", year: "numeric" })}
              </div>
              <div className="col-span-2" style={{ color: "var(--ev-muted)" }}>{r.time}</div>
              <div className="col-span-2">
                <Select value={r.status || "confirmed"} onValueChange={(v) => onStatusChange(r.id, v)}>
                  <SelectTrigger data-testid={ADMIN.bookingStatus(r.id)}
                                 className="rounded-full h-8 py-0 border hairline bg-white text-xs">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {Object.keys(BOOKING_STATUS_META).map((k) => (
                      <SelectItem key={k} value={k}>{BOOKING_STATUS_META[k].label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="md:hidden divide-y hairline">
        {rows.map((r) => (
          <div key={r.id} data-testid={ADMIN.bookingRow(r.id)} className="p-4 flex flex-col gap-2">
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <div className="font-medium truncate">{r.user_name}</div>
                <div className="text-xs truncate" style={{ color: "var(--ev-muted)" }}>{r.user_email}</div>
              </div>
              <StatusPill meta={BOOKING_STATUS_META[r.status || "confirmed"]} />
            </div>
            <div className="text-sm font-serif-ev">{r.program_title || "Consultation"}</div>
            <div className="text-xs" style={{ color: "var(--ev-muted)" }}>
              {new Date(r.date).toLocaleDateString(undefined, { day: "2-digit", month: "short", year: "numeric" })} · {r.time}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function InquiryDrawer({ inquiry, onSave, onDelete, onClose }) {
  const [notes, setNotes] = useState(inquiry.admin_notes || "");
  const [status, setStatus] = useState(inquiry.status || "new");
  useEffect(() => {
    setNotes(inquiry.admin_notes || "");
    setStatus(inquiry.status || "new");
  }, [inquiry.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const save = async () => {
    const updates = {};
    if (notes !== (inquiry.admin_notes || "")) updates.admin_notes = notes;
    if (status !== (inquiry.status || "new")) updates.status = status;
    if (Object.keys(updates).length === 0) { onClose(); return; }
    await onSave(updates);
  };

  return (
    <>
      <div className="flex-1 overflow-y-auto pr-1">
        <SheetHeader className="text-left">
          <div className="eyebrow">Inquiry</div>
          <SheetTitle className="font-serif-ev text-3xl leading-tight tracking-tight mt-2">
            {inquiry.name}
          </SheetTitle>
          <SheetDescription>
            Received {formatDate(inquiry.created_at)}
          </SheetDescription>
        </SheetHeader>

        <div className="mt-6 space-y-4 text-sm">
          <a href={`mailto:${inquiry.email}`} className="flex items-center gap-2 hover:underline">
            <Mail className="w-4 h-4" style={{ color: "var(--ev-blue-2)" }} /> {inquiry.email}
          </a>
          {inquiry.phone && (
            <a href={`tel:${inquiry.phone}`} className="flex items-center gap-2 hover:underline">
              <Phone className="w-4 h-4" style={{ color: "var(--ev-blue-2)" }} /> {inquiry.phone}
            </a>
          )}
          {inquiry.location && (
            <div className="flex items-center gap-2" style={{ color: "var(--ev-muted)" }}>
              <MapPin className="w-4 h-4" style={{ color: "var(--ev-blue-2)" }} /> {inquiry.location}
            </div>
          )}
          {inquiry.area_of_interest && (
            <div className="flex items-center gap-2" style={{ color: "var(--ev-muted)" }}>
              <Sparkles className="w-4 h-4" style={{ color: "var(--ev-blue-2)" }} /> {inquiry.area_of_interest}
            </div>
          )}
        </div>

        {(inquiry.intention || inquiry.message) && (
          <div className="mt-6 ev-card-cream p-5">
            <div className="eyebrow">Their words</div>
            {inquiry.intention && <div className="mt-2 text-sm">{inquiry.intention}</div>}
            {inquiry.message && (
              <p className="mt-3 text-sm leading-relaxed" style={{ color: "var(--ev-ink-2)" }}>
                {inquiry.message}
              </p>
            )}
          </div>
        )}

        <div className="mt-6">
          <div className="text-xs uppercase tracking-widest mb-2" style={{ color: "var(--ev-muted)" }}>Status</div>
          <Select value={status} onValueChange={setStatus}>
            <SelectTrigger className="rounded-full bg-white border hairline"><SelectValue /></SelectTrigger>
            <SelectContent>
              {Object.keys(STATUS_META).map((k) => (
                <SelectItem key={k} value={k}>{STATUS_META[k].label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="mt-4 mb-6">
          <div className="text-xs uppercase tracking-widest mb-2" style={{ color: "var(--ev-muted)" }}>Private notes</div>
          <textarea
            data-testid={ADMIN.drawerNotes}
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            rows={5}
            placeholder="Add a quiet note for yourself — a follow-up date, the tone of your call, anything."
            className="w-full bg-white border hairline rounded-2xl py-3 px-4 focus:outline-none focus:border-[color:var(--ev-blue-2)] text-[15px]"
          />
        </div>
      </div>

      <div className="border-t hairline pt-4 mt-2 flex items-center justify-between gap-3"
           style={{ background: "var(--ev-bg)" }}>
        <button
          onClick={onDelete}
          className="inline-flex items-center gap-2 text-sm px-4 py-2"
          style={{ color: "#B4553F" }}
        >
          <Trash2 className="w-4 h-4" /> Delete
        </button>
        <div className="flex items-center gap-3">
          <button data-testid={ADMIN.drawerClose} onClick={onClose} className="ev-btn ev-btn-outline">Close</button>
          <button data-testid={ADMIN.drawerSave} onClick={save} className="ev-btn ev-btn-primary">Save</button>
        </div>
      </div>
    </>
  );
}
